import type { ZoneId } from "../data/map.ts";
import { SCREEN_H, SCREEN_W, type Gfx } from "../engine/gfx.ts";
import { bayer, fbm, hash2, vnoise } from "../world/noise.ts";
import { hex, mix, type Col } from "./pal.ts";

/**
 * Painted battle backdrops, one per zone and time of day: gradient sky with shaded clouds, two
 * parallax silhouette bands (hills / skyline / cliffs / forest), a perspective ground and the two
 * platforms. Baked once per key into a 384×216 image.
 */

export type Tod = "day" | "dusk" | "night" | "dawn";

export function todFor(hour: number): Tod {
  if (hour < 5 || hour >= 20.5) return "night";
  if (hour < 7.5) return "dawn";
  if (hour >= 17.5) return "dusk";
  return "day";
}

export const HORIZON = 100;
export const FOE_PAD = { x: 280, y: 104, rx: 62, ry: 15 };
export const YOU_PAD = { x: 104, y: 178, rx: 82, ry: 20 };

interface Theme {
  skyTop: Col;
  skyLow: Col;
  far: Col;
  mid: Col;
  midHi: Col;
  ground: readonly Col[];
  pad: readonly Col[];
  padSide: Col;
  kind: "hills" | "town" | "pond" | "cliffs" | "forest" | "canyon" | "snow" | "dunes" | "volcano" | "bog" | "crystal"
    | "cave" | "reef" | "sky" | "cyber" | "autumn" | "haunted" | "jungle" | "blossom" | "ruins";
}

const THEMES: Record<ZoneId, Theme> = {
  stubtown: { skyTop: hex("#6fb8ff"), skyLow: hex("#d9f1ff"), far: hex("#9cc0d8"), mid: hex("#c86a5c"), midHi: hex("#f0a080"),
    ground: [hex("#e2dccc"), hex("#d0c8b4"), hex("#bdb29a"), hex("#a49880")], pad: [hex("#f4b98e"), hex("#e39a74"), hex("#c97a5c")], padSide: hex("#7a4440"), kind: "town" },
  drippond: { skyTop: hex("#62b4ff"), skyLow: hex("#d4f2ff"), far: hex("#8fc9b0"), mid: hex("#3f8a4c"), midHi: hex("#6fbf5a"),
    ground: [hex("#a6dd6a"), hex("#86c95a"), hex("#6aae4c"), hex("#4e8f40")], pad: [hex("#b6e878"), hex("#86c95a"), hex("#5e9e48")], padSide: hex("#7a5a3e"), kind: "pond" },
  copeCliffs: { skyTop: hex("#78bcff"), skyLow: hex("#e0f2ff"), far: hex("#a9b8cc"), mid: hex("#a57a5a"), midHi: hex("#d9ac80"),
    ground: [hex("#b4d878"), hex("#98c462"), hex("#7aa652"), hex("#5c8646")], pad: [hex("#c8e488"), hex("#98c462"), hex("#6e9a4e")], padSide: hex("#86604a"), kind: "cliffs" },
  chartwood: { skyTop: hex("#5aa8f0"), skyLow: hex("#cdeeff"), far: hex("#5f9c7a"), mid: hex("#26593c"), midHi: hex("#3f8a4c"),
    ground: [hex("#8ed262"), hex("#6fbc52"), hex("#55a046"), hex("#3e8040")], pad: [hex("#a2e06e"), hex("#6fbc52"), hex("#4a8a44")], padSide: hex("#6a4a36"), kind: "forest" },
  gasGulch: { skyTop: hex("#f0a878"), skyLow: hex("#ffe2b8"), far: hex("#e0a07a"), mid: hex("#b8603e"), midHi: hex("#e88a5a"),
    ground: [hex("#f0cc92"), hex("#dcb07a"), hex("#c09064"), hex("#9a6e50")], pad: [hex("#f6d8a0"), hex("#dcb07a"), hex("#b08660")], padSide: hex("#7c5440"), kind: "canyon" },
  yieldYard: { skyTop: hex("#8ec4f0"), skyLow: hex("#eef8ff"), far: hex("#c6d8ea"), mid: hex("#3c6a5a"), midHi: hex("#6a9a84"),
    ground: [hex("#eef6f8"), hex("#d6e8ee"), hex("#b6d0da"), hex("#94b0c0")], pad: [hex("#ffffff"), hex("#dcecf2"), hex("#a8c4d2")], padSide: hex("#6a7e92"), kind: "snow" },
  frontierPost: { skyTop: hex("#7ab0f0"), skyLow: hex("#ffe8c8"), far: hex("#d8b48a"), mid: hex("#c47a4a"), midHi: hex("#f0b080"),
    ground: [hex("#ecd6b0"), hex("#dcc098"), hex("#c8a880"), hex("#a88a68")], pad: [hex("#f0c890"), hex("#d8a870"), hex("#b8885a")], padSide: hex("#7a5238"), kind: "town" },
  frostfloor: { skyTop: hex("#6aa0e0"), skyLow: hex("#e8f6ff"), far: hex("#b8d0ec"), mid: hex("#4a6e8c"), midHi: hex("#8ab4d4"),
    ground: [hex("#f8fcff"), hex("#dff0fa"), hex("#bcd8ec"), hex("#98b8d4")], pad: [hex("#ffffff"), hex("#d6ecfa"), hex("#9ec4e0")], padSide: hex("#5a7896"), kind: "snow" },
  crystalCache: { skyTop: hex("#2a1e5a"), skyLow: hex("#8a6ad0"), far: hex("#6a58b0"), mid: hex("#3e2e78"), midHi: hex("#9a80f0"),
    ground: [hex("#b8a8e8"), hex("#9888d0"), hex("#7a68b4"), hex("#5a4a94")], pad: [hex("#e0d4ff"), hex("#b8a4f0"), hex("#8a74d0")], padSide: hex("#3e2e6e"), kind: "crystal" },
  candleDunes: { skyTop: hex("#f09a5a"), skyLow: hex("#ffe6a8"), far: hex("#f0c080"), mid: hex("#e0a060"), midHi: hex("#ffd090"),
    ground: [hex("#ffe2a8"), hex("#f4cc88"), hex("#e0b06c"), hex("#c08e54")], pad: [hex("#fff0c0"), hex("#f0d090"), hex("#d0a868")], padSide: hex("#8a6038"), kind: "dunes" },
  rugmire: { skyTop: hex("#5a7a6a"), skyLow: hex("#c8d8a8"), far: hex("#7a9478"), mid: hex("#3a5238"), midHi: hex("#5f7a48"),
    ground: [hex("#a8b878"), hex("#8a9c5c"), hex("#6c7e48"), hex("#4e5e38")], pad: [hex("#b8c888"), hex("#8ca862"), hex("#5f7a48")], padSide: hex("#3a3a2a"), kind: "bog" },
  magmaMint: { skyTop: hex("#3a1a2a"), skyLow: hex("#e0643a"), far: hex("#5a2a2e"), mid: hex("#2a1e24"), midHi: hex("#6a3a34"),
    ground: [hex("#6a5a60"), hex("#524650"), hex("#3e3440"), hex("#2c2430")], pad: [hex("#7a6a70"), hex("#5a4c58"), hex("#3e3442")], padSide: hex("#1e1620"), kind: "volcano" },
  neonHarbor: { skyTop: hex("#0c0826"), skyLow: hex("#5a2a8a"), far: hex("#2a1a5a"), mid: hex("#1a1238"), midHi: hex("#ff5ad8"),
    ground: [hex("#3a3a5a"), hex("#2e2e4a"), hex("#24243c"), hex("#1a1a2e")], pad: [hex("#5a5a80"), hex("#44446a"), hex("#303052")], padSide: hex("#121226"), kind: "cyber" },
  neonGrid: { skyTop: hex("#07041a"), skyLow: hex("#6a1a7a"), far: hex("#241450"), mid: hex("#140c30"), midHi: hex("#3ef0e0"),
    ground: [hex("#2a1a4a"), hex("#22143e"), hex("#1a0e32"), hex("#120a26")], pad: [hex("#4a3a7a"), hex("#3a2c64"), hex("#2a1e4e")], padSide: hex("#0e0820"), kind: "cyber" },
  mempoolCaverns: { skyTop: hex("#070a16"), skyLow: hex("#1e2c48"), far: hex("#1a2438"), mid: hex("#101828"), midHi: hex("#2a3a58"),
    ground: [hex("#3a4660"), hex("#2e384e"), hex("#242c3e"), hex("#1a2030")], pad: [hex("#56647e"), hex("#424e66"), hex("#303a50")], padSide: hex("#141a28"), kind: "cave" },
  coralLedger: { skyTop: hex("#3aa4ff"), skyLow: hex("#d8f8ff"), far: hex("#5ac8d8"), mid: hex("#2e8a5a"), midHi: hex("#5ac070"),
    ground: [hex("#fff4d4"), hex("#f6e2b0"), hex("#e8cc90"), hex("#d0ae74")], pad: [hex("#ffd0c0"), hex("#ffa488"), hex("#e0786a")], padSide: hex("#8a4a4a"), kind: "reef" },
  cloudChain: { skyTop: hex("#2a4ec8"), skyLow: hex("#e6eeff"), far: hex("#b8c8f0"), mid: hex("#ffffff"), midHi: hex("#ffffff"),
    ground: [hex("#ffffff"), hex("#eef4ff"), hex("#d8e2f8"), hex("#bcc8e8")], pad: [hex("#fff6d8"), hex("#f2e0a0"), hex("#d8bc70")], padSide: hex("#8a7a9a"), kind: "sky" },
  bridgeport: { skyTop: hex("#6aa8f0"), skyLow: hex("#ffe4d0"), far: hex("#b8a8c8"), mid: hex("#5a6ab0"), midHi: hex("#8a9ae0"),
    ground: [hex("#e8dcc8"), hex("#d4c6ae"), hex("#bcae94"), hex("#a0927a")], pad: [hex("#f4b98e"), hex("#e39a74"), hex("#c97a5c")], padSide: hex("#7a4440"), kind: "town" },
  halvingHarvest: { skyTop: hex("#6ab0f0"), skyLow: hex("#ffe2b0"), far: hex("#e8c07a"), mid: hex("#c8602f"), midHi: hex("#ffb85a"),
    ground: [hex("#ffe29a"), hex("#f2c870"), hex("#dcaa56"), hex("#b88840")], pad: [hex("#fff0b0"), hex("#f2d27a"), hex("#dcae52")], padSide: hex("#8a6030"), kind: "autumn" },
  ghostchain: { skyTop: hex("#140f2a"), skyLow: hex("#5a4a7a"), far: hex("#2e2644"), mid: hex("#1e1a30"), midHi: hex("#6a6488"),
    ground: [hex("#5a5674"), hex("#48445e"), hex("#38344c"), hex("#2a263a")], pad: [hex("#7a7694"), hex("#5e5a78"), hex("#48445e")], padSide: hex("#1c1a2a"), kind: "haunted" },
  pumpJungle: { skyTop: hex("#4aa8e0"), skyLow: hex("#d8f4d0"), far: hex("#3a8a5a"), mid: hex("#1e6632"), midHi: hex("#4fae4a"),
    ground: [hex("#7cc858"), hex("#58aa48"), hex("#3e8c3e"), hex("#2c6e36")], pad: [hex("#a8e070"), hex("#7cc858"), hex("#4f9a45")], padSide: hex("#5e4832"), kind: "jungle" },
  sakuraStake: { skyTop: hex("#7ac0ff"), skyLow: hex("#ffe8f2"), far: hex("#a8b8d8"), mid: hex("#e88ab0"), midHi: hex("#ffc6dc"),
    ground: [hex("#d4eca8"), hex("#b8dc8c"), hex("#98c674"), hex("#7aac60")], pad: [hex("#f4efe6"), hex("#e2dace"), hex("#cac0b0")], padSide: hex("#8a7e70"), kind: "blossom" },
  rugpullRuins: { skyTop: hex("#6aaee8"), skyLow: hex("#ffe8c0"), far: hex("#d8c8a0"), mid: hex("#8a7a5a"), midHi: hex("#c2a87c"),
    ground: [hex("#e0cca4"), hex("#c8b28a"), hex("#ac9670"), hex("#8e7a5a")], pad: [hex("#f0e4c4"), hex("#d8c8a0"), hex("#b8a47c")], padSide: hex("#6e5a42"), kind: "ruins" },
};

function todTint(c: Col, tod: Tod, y: number): Col {
  if (tod === "day") return c;
  if (tod === "night") return mix(c, hex("#141a3c"), 0.62 - (y / SCREEN_H) * 0.12);
  if (tod === "dusk") return mix(c, y < HORIZON ? hex("#ff8a5a") : hex("#8a4a6a"), 0.34);
  return mix(c, hex("#ffb0c8"), 0.22);
}

const cache = new Map<string, ImageData>();

export function battleBackdrop(zone: ZoneId, tod: Tod): ImageData {
  const key = `${zone}:${tod}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const th = THEMES[zone];
  const img = new ImageData(SCREEN_W, SCREEN_H);
  const d = img.data;
  const put = (x: number, y: number, c: Col) => {
    if (x < 0 || y < 0 || x >= SCREEN_W || y >= SCREEN_H) return;
    const o = (y * SCREEN_W + x) * 4;
    const t = todTint(c, tod, y);
    d[o] = t[0]; d[o + 1] = t[1]; d[o + 2] = t[2]; d[o + 3] = 255;
  };
  const seed = zone.length * 13;
  // Sky.
  for (let y = 0; y < HORIZON; y++) {
    for (let x = 0; x < SCREEN_W; x++) {
      const t = y / HORIZON + (bayer(x, y) - 0.5) * 0.06;
      put(x, y, mix(th.skyTop, th.skyLow, Math.max(0, Math.min(1, t * t * 1.2))));
    }
  }
  if ((tod === "night" && th.kind !== "cave") || th.kind === "cyber" || th.kind === "haunted") {
    for (let i = 0; i < 90; i++) {
      const x = Math.floor(hash2(i, 1, seed) * SCREEN_W), y = Math.floor(hash2(i, 2, seed) * (HORIZON - 20));
      const o = (y * SCREEN_W + x) * 4;
      d[o] = d[o + 1] = 255; d[o + 2] = 240;
    }
  }
  if (th.kind === "haunted") {
    // Big pale moon over the far right, clear of the foe's plate and platform.
    for (let y = 4; y < 50; y++) for (let x = 318; x < 372; x++) {
      const dx = (x - 346) / 20, dy = (y - 24) / 20;
      const r2 = dx * dx + dy * dy;
      if (r2 < 1) put(x, y, mix(hex("#f4f0ff"), hex("#c8c0e0"), vnoise(x / 6, y / 6, 7) * 0.5 + r2 * 0.3));
      else if (r2 < 1.5 && bayer(x, y) > 0.55) put(x, y, mix(th.skyLow, hex("#8a80b0"), 0.4));
    }
  }
  // Clouds (none underground, under the neon haze or over the graves).
  for (let y = 4; y < (th.kind === "cave" || th.kind === "cyber" || th.kind === "haunted" ? 0 : HORIZON - 26); y++) {
    for (let x = 0; x < SCREEN_W; x++) {
      const n = fbm(x / 70 + seed, y / 18, 4, seed) - (y / HORIZON) * 0.25;
      if (n < 0.56) continue;
      const lit = fbm((x - 3) / 70 + seed, (y - 4) / 18, 4, seed) - ((y - 4) / HORIZON) * 0.25;
      const c = lit > n + 0.02 ? hex("#ffffff") : n > 0.63 ? hex("#eef6ff") : hex("#d6e6f6");
      put(x, y, n < 0.58 && bayer(x, y) > 0.5 ? mix(c, th.skyLow, 0.5) : c);
    }
  }
  // Far band.
  for (let x = 0; x < SCREEN_W; x++) {
    let top: number;
    let bottom = HORIZON;
    if (th.kind === "cliffs" || th.kind === "canyon") top = HORIZON - 44 + Math.round(fbm(x / 40, 1, 3, seed) * 26) - (Math.floor(x / 37) % 3) * 4;
    else if (th.kind === "town") top = HORIZON - 24 + Math.round(vnoise(x / 60, 2, seed) * 10);
    else if (th.kind === "dunes") top = HORIZON - 22 + Math.round(Math.sin(x / 47 + seed) * 7 + Math.sin(x / 19) * 2);
    else if (th.kind === "volcano") top = Math.max(HORIZON - 70, HORIZON - 70 + Math.round(Math.abs(x - 250) * 0.62 + vnoise(x / 10, 3, seed) * 4));
    else if (th.kind === "crystal") {
      const cell = Math.floor(x / 23), cx = cell * 23 + 11;
      top = HORIZON - 16 - Math.round(Math.max(0, (1 - Math.abs(x - cx) / 11)) * (18 + hash2(cell, 1, seed) * 30));
    }
    else if (th.kind === "cave") top = HORIZON - 40 + Math.round(fbm(x / 30, 4, 3, seed) * 30);
    else if (th.kind === "reef") top = HORIZON - 3 - (x > 60 && x < 130 ? Math.round(Math.max(0, 1 - Math.abs(x - 95) / 35) * 12) : 0);
    else if (th.kind === "sky") {
      // Distant floating islands.
      const cell = Math.floor(x / 64), cx = cell * 64 + 32 + (hash2(cell, 2, seed) - 0.5) * 20;
      const w = 16 + hash2(cell, 3, seed) * 14;
      const u = Math.abs(x - cx) / w;
      top = u < 1 ? HORIZON - 34 - Math.round(hash2(cell, 4, seed) * 20) + Math.round(u * u * 5) : HORIZON;
      // Islands float: a rock underside that tapers to a point instead of reaching the horizon.
      bottom = u < 1 ? top + 4 + Math.round((1 - u * u) * 16) : HORIZON;
    }
    else if (th.kind === "cyber") {
      const b = Math.floor(x / 17);
      top = HORIZON - 20 - Math.round(hash2(b, 6, seed) * 40);
    }
    else if (th.kind === "blossom") {
      // One distant snow-capped peak over rolling foothills.
      const peak = HORIZON - 72 + Math.round(Math.abs(x - 150) * 0.55);
      top = Math.min(peak, HORIZON - 22 + Math.round(fbm(x / 60, 2, 3, seed) * 12));
    }
    else if (th.kind === "ruins") {
      // Broken colonnade on the skyline: columns of uneven height with gaps.
      const cell = Math.floor(x / 19), cx = x - cell * 19;
      const standing = hash2(cell, 11, seed) > 0.3;
      top = standing && cx > 4 && cx < 13 ? HORIZON - 24 - Math.round(hash2(cell, 12, seed) * 36) : HORIZON - 14 + Math.round(vnoise(x / 12, 3, seed) * 6);
    }
    else if (th.kind === "haunted") {
      // Dead-tree skyline: bare trunks with spiky crowns.
      const cell = Math.floor(x / 23), cx = x - cell * 23 - 11;
      const hgt = 26 + hash2(cell, 13, seed) * 30;
      const w = Math.max(0, 8 - Math.abs(cx) * (Math.abs(cx) < 2 ? 0 : 1.1));
      top = HORIZON - 12 - Math.round(Math.abs(cx) < 2 ? hgt : w * hgt / 10 * hash2(cell, x, seed));
    }
    else if (th.kind === "jungle") top = HORIZON - 38 + Math.round(fbm(x / 50, 2, 3, seed) * 26);
    else top = HORIZON - 30 + Math.round(fbm(x / 90, 2, 3, seed) * 22);
    for (let y = top; y < Math.min(HORIZON, bottom); y++) {
      const shade = (y - top) / Math.max(1, HORIZON - top);
      let c = mix(th.far, th.skyLow, 0.35 - shade * 0.25);
      if (th.kind === "cliffs" || th.kind === "canyon") {
        if ((x + Math.floor(y / 6) * 3) % 17 === 0) c = mix(c, hex("#5a4a5a"), 0.25);
        if (y === top) c = mix(c, hex("#ffffff"), 0.3);
      }
      if (th.kind === "snow" && y - top < 4) c = hex("#ffffff");
      if (th.kind === "volcano" && top < HORIZON - 62 && y - top < 3) c = hex("#ffb050");
      if (th.kind === "crystal" && (x % 23 === 11 || y - top < 2)) c = mix(c, hex("#ffffff"), 0.35);
      if (th.kind === "sky" && top < HORIZON) {
        // Island: grass cap, rock underside that tapers off.
        c = y - top < 3 ? hex("#b8e0a0") : mix(hex("#9a8ab0"), th.skyLow, 0.4);
      }
      if (th.kind === "cyber") {
        const bx = x % 17;
        c = bx === 0 || bx === 16 ? mix(th.far, hex("#000000"), 0.4) : th.far;
        if (bx > 2 && bx < 14 && (y - top) % 5 === 2 && hash2(x >> 1, y, seed) > 0.35) c = hash2(x >> 2, y, 7) > 0.5 ? hex("#ff5ad8") : hex("#3ef0e0");
        if (y === top) c = hex("#ff5ad8");
      }
      if (th.kind === "cave") c = mix(th.far, hex("#000000"), shade * 0.4);
      if (th.kind === "blossom" && y - top < 6 && top < HORIZON - 40) c = hex("#ffffff");
      if (th.kind === "ruins" && (x % 19 === 5 || x % 19 === 12)) c = mix(c, hex("#000000"), 0.12);
      if (th.kind === "ruins" && y - top < 2) c = mix(c, hex("#ffffff"), 0.35);
      if (th.kind === "haunted") c = mix(th.far, th.skyLow, 0.15);
      if (th.kind === "jungle" && (y - top) > 6 && fbm(x / 20, y / 8, 2, seed + 3) > 0.6) c = mix(c, hex("#ffffff"), 0.25);
      put(x, y, c);
    }
  }
  // Cave ceiling: stalactites hanging into the dark, with glowing drips.
  if (th.kind === "cave") {
    for (let x = 0; x < SCREEN_W; x++) {
      const cell = Math.floor(x / 13), cx = cell * 13 + 6;
      const len = 8 + Math.round(hash2(cell, 9, seed) * 26);
      const half = Math.max(0, 5 - Math.abs(x - cx));
      const bottom = Math.round(10 + (half / 5) * len);
      for (let y = 0; y < bottom; y++) put(x, y, mix(hex("#1a2438"), hex("#000000"), y < 10 ? 0.3 : 0));
      if (half === 5 && hash2(cell, 10, seed) > 0.5) put(x, bottom + 2, hex("#5ae8d0"));
    }
  }
  // Synthwave sun behind the grid city.
  if (th.kind === "cyber") {
    for (let y = HORIZON - 58; y < HORIZON - 18; y++) {
      for (let x = 250; x < 330; x++) {
        const dx = (x - 290) / 38, dy = (y - (HORIZON - 30)) / 38;
        if (dx * dx + dy * dy > 1 || (y > HORIZON - 44 && (y - (HORIZON - 44)) % 6 < 2)) continue;
        const o = (y * SCREEN_W + x) * 4;
        void o;
        put(x, y, mix(hex("#ffd060"), hex("#ff3aa0"), (y - (HORIZON - 58)) / 40));
      }
    }
  }
  // Volcano glow over the crater and a plume of ash.
  if (th.kind === "volcano") {
    for (let y = HORIZON - 96; y < HORIZON - 60; y++) {
      for (let x = 220; x < 282; x++) {
        const dx = (x - 250) / 30, dy = (y - (HORIZON - 64)) / 30;
        const r = Math.sqrt(dx * dx + dy * dy);
        if (r > 1) continue;
        const n = fbm(x / 12, y / 9, 3, seed);
        if (n > 0.52) put(x, y, mix(hex("#6a5a66"), hex("#ff8a4a"), Math.max(0, 1 - r) * 0.6));
      }
    }
  }
  // Mid band.
  const bumps = (x: number, r: number, s: number) => {
    const cell = Math.floor(x / r);
    let best = 99;
    for (let k = -1; k <= 1; k++) {
      const cx = (cell + k) * r + r / 2 + (hash2(cell + k, 3, s) - 0.5) * r * 0.6;
      const rr = r * (0.6 + hash2(cell + k, 4, s) * 0.5);
      const dx = x - cx;
      if (Math.abs(dx) < rr) best = Math.min(best, -Math.sqrt(rr * rr - dx * dx));
    }
    return best;
  };
  for (let x = 0; x < SCREEN_W; x++) {
    let top: number;
    if (th.kind === "town") {
      const b = Math.floor(x / 44);
      const bx = x - b * 44;
      const hgt = 18 + Math.floor(hash2(b, 5, seed) * 14);
      const roof = bx < 22 ? bx : 44 - bx;
      top = HORIZON - hgt - Math.min(10, Math.floor(roof / 2));
      for (let y = top; y < HORIZON + 4; y++) {
        const isRoof = y < HORIZON - hgt + 2;
        let c = isRoof ? mix(th.mid, hex("#000000"), bx > 22 ? 0.2 : 0) : mix(hex("#e8d8c0"), hex("#b8a890"), bx > 30 ? 0.5 : 0);
        if (!isRoof && (bx % 11 === 4 || bx % 11 === 5) && (y - top) % 10 > 3 && (y - top) % 10 < 8) c = tod === "night" || tod === "dusk" ? hex("#ffd070") : hex("#7ab0e0");
        put(x, y, c);
      }
      continue;
    }
    if (th.kind === "forest" || th.kind === "pond" || th.kind === "snow" || th.kind === "bog") top = HORIZON - 6 + Math.round(bumps(x, th.kind === "forest" ? 26 : 18, seed) * (th.kind === "forest" ? 1.3 : 0.9));
    else if (th.kind === "autumn" || th.kind === "blossom") top = HORIZON - 4 + Math.round(bumps(x, 22, seed) * 1.1);
    else if (th.kind === "jungle") top = HORIZON - 4 + Math.round(bumps(x, 30, seed) * 1.6);
    else if (th.kind === "haunted") {
      // Tombstone silhouettes along the horizon.
      const cell = Math.floor(x / 15), cx = x - cell * 15;
      const hasStone = hash2(cell, 14, seed) > 0.35 && cx > 3 && cx < 11;
      top = hasStone ? HORIZON - 10 - Math.round(hash2(cell, 15, seed) * 6) + (cx === 4 || cx === 10 ? 2 : 0) : HORIZON - 2;
    }
    else if (th.kind === "ruins") top = HORIZON - 6 + Math.round(vnoise(x / 9, 7, seed) * 5);
    else if (th.kind === "cliffs" || th.kind === "canyon" || th.kind === "volcano") top = HORIZON - 12 + Math.round(vnoise(x / 20, 7, seed) * 8);
    else if (th.kind === "dunes") top = HORIZON - 8 + Math.round(Math.sin(x / 31 + 2) * 4);
    else if (th.kind === "crystal") top = HORIZON - 10 + Math.round(Math.abs(Math.sin(x / 7)) * -6 + vnoise(x / 8, 7, seed) * 4);
    else if (th.kind === "cave") top = HORIZON - 14 + Math.round(vnoise(x / 14, 7, seed) * 12);
    else if (th.kind === "reef") top = HORIZON + 6;
    else if (th.kind === "sky") top = HORIZON - 4 + Math.round(bumps(x, 22, seed) * 0.5);
    else if (th.kind === "cyber") {
      const b = Math.floor(x / 29);
      top = HORIZON - 8 - Math.round(hash2(b, 8, seed) * 14);
    }
    else top = HORIZON - 8;
    for (let y = top; y < HORIZON + 6; y++) {
      const edge = y - top;
      let c = edge < 2 ? th.midHi : th.mid;
      if (edge >= 2 && hash2(x, y, seed) < 0.12) c = th.midHi;
      if (th.kind === "snow" && edge < 3) c = hex("#ffffff");
      if (th.kind === "sky") c = edge < 3 ? hex("#ffffff") : mix(hex("#eef4ff"), hex("#c8d4f0"), Math.min(1, edge / 10));
      if (th.kind === "cyber") {
        const bx = x % 29;
        c = bx < 2 || bx > 26 ? hex("#0a0618") : th.mid;
        if (edge === 0) c = th.midHi;
        else if (bx > 4 && bx < 24 && edge % 3 === 1 && hash2(x, y, seed + 5) > 0.55) c = hash2(x, y, 8) > 0.6 ? hex("#b4ff5a") : hex("#3ef0e0");
      }
      if (th.kind === "cave" && edge >= 2 && hash2(x, y, seed + 6) < 0.02) c = hex("#5ae8d0");
      if (th.kind === "autumn" && edge >= 2 && hash2(x, y, seed + 7) < 0.14) c = [hex("#ffd060"), hex("#c8402c"), hex("#ef8a3f")][Math.floor(hash2(x, y, 3) * 3)]!;
      if (th.kind === "blossom" && edge >= 2 && hash2(x, y, seed + 8) < 0.16) c = hex("#ffe1ec");
      if (y > HORIZON && th.kind !== "sky") c = mix(c, hex("#000000"), 0.15);
      put(x, y, c);
    }
  }
  // Tropical sea between the horizon and the beach.
  if (th.kind === "reef") {
    for (let y = HORIZON - 2; y < HORIZON + 16; y++) {
      for (let x = 0; x < SCREEN_W; x++) {
        const edge = Math.sin(x / 19 + seed) * 2;
        if (y > HORIZON + 14 + edge) continue;
        let c = mix(hex("#2a9ae8"), hex("#5ae0e0"), Math.max(0, (y - HORIZON + 2) / 18));
        if ((x * 3 + y * 11) % 29 < 3 && bayer(x, y) > 0.4) c = hex("#e8ffff");
        if (y > HORIZON + 11 + edge) c = hex("#f4fcff");
        put(x, y, c);
      }
    }
    // Two palm silhouettes on the far island.
    for (const px of [84, 104]) {
      for (let y = HORIZON - 30; y < HORIZON - 4; y++) put(px + Math.round((y - (HORIZON - 30)) * 0.15), y, hex("#3a5a3a"));
      for (let k = 0; k < 5; k++) {
        const a = -2.6 + k * 0.6;
        for (let r = 0; r < 12; r++) put(px + Math.round(Math.cos(a) * r), HORIZON - 30 + Math.round(Math.sin(a) * r * 0.6 + r * r * 0.02), hex("#2e6a3a"));
      }
    }
  }
  // Pond band for Drippond.
  if (th.kind === "pond") {
    for (let y = HORIZON + 2; y < HORIZON + 14; y++) {
      for (let x = 0; x < SCREEN_W; x++) {
        const edge = Math.sin(x / 23 + seed) * 2;
        if (y < HORIZON + 3 + edge || y > HORIZON + 12 + edge) continue;
        let c = mix(hex("#5cc0e8"), hex("#2a6bb8"), (y - HORIZON) / 16);
        if ((x + y * 7) % 23 < 3 && bayer(x, y) > 0.3) c = hex("#dff6ff");
        put(x, y, c);
      }
    }
  }
  // Ground in perspective.
  const g = th.ground;
  for (let y = HORIZON + 4; y < SCREEN_H; y++) {
    const depth = (y - HORIZON) / (SCREEN_H - HORIZON);
    for (let x = 0; x < SCREEN_W; x++) {
      if ((th.kind === "pond" || th.kind === "reef") && y < HORIZON + 16) {
        const o = (y * SCREEN_W + x) * 4;
        if (d[o + 3]) continue;
      }
      const stripe = Math.floor((x - SCREEN_W / 2) / (18 + depth * 60) + Math.sin(y / 9) * 0.2);
      let f = 1 + (stripe & 1) * 0.6 + (vnoise(x / (12 + depth * 30), y / (4 + depth * 10), seed) - 0.5) * 1.2 + (1 - depth) * 0.6;
      if (hash2(x, y, seed + 1) < 0.05 + depth * 0.04) f -= 1;
      else if (hash2(x, y - 1, seed + 1) < 0.05 + depth * 0.04) f += 1;
      const i = Math.max(0, Math.min(g.length - 1, Math.floor(f + bayer(x, y) * 0.9)));
      let c = g[i]!;
      if (th.kind === "town") {
        const rowH = Math.max(3, Math.round(5 + depth * 12));
        const row = Math.floor((y - HORIZON) / rowH);
        const slabW = Math.round(14 + depth * 30);
        if ((y - HORIZON) % rowH === 0 || (x - SCREEN_W / 2 + (row % 2) * (slabW >> 1) + 10000) % slabW === 0) c = g[3]!;
        else c = g[Math.min(2, Math.floor(hash2(Math.floor((x + (row % 2) * (slabW >> 1)) / slabW), row, 3) * 2.2))]!;
      }
      if (th.kind === "forest" && hash2(x, y, 88) < 0.006) c = [hex("#ff8fb0"), hex("#ffe070"), hex("#ffffff")][Math.floor(hash2(x, y, 89) * 3)]!;
      if (th.kind === "canyon" && vnoise(x / 9, y / 3, 90) > 0.82) c = g[3]!;
      if (th.kind === "dunes" && Math.sin((x + y * 3.2) / (4 + depth * 9) + vnoise(x / 30, y / 10, 5) * 3) > 0.86) c = g[Math.min(3, i + 1)]!;
      if (th.kind === "volcano") {
        const crack = Math.abs(vnoise(x / (10 + depth * 18), y / (3 + depth * 6), 91) - 0.5);
        if (crack < 0.018) c = hex("#ff9a3c");
        else if (crack < 0.03) c = hex("#c8402c");
      }
      if (th.kind === "bog" && vnoise(x / (16 + depth * 30), y / (4 + depth * 8), 92) > 0.72) c = mix(hex("#3a5046"), hex("#6a8a78"), bayer(x, y) * 0.4);
      if (th.kind === "crystal" && hash2(x, y, 93) < 0.004) c = [hex("#ffffff"), hex("#a4f3ea"), hex("#f0c8ff")][Math.floor(hash2(x, y, 94) * 3)]!;
      if (th.kind === "cyber") {
        // Synthwave floor: receding horizontal lines and converging verticals.
        const z = 1 / Math.max(0.02, depth);
        const hz = (z * 6) % 1;
        // Converging verticals: evenly spaced on the floor plane, thin on screen.
        const X = (x - SCREEN_W / 2) / Math.max(0.05, depth);
        const off = Math.abs(X - Math.round(X / 56) * 56) * depth;
        if ((hz < 0.08 + depth * 0.05 && depth > 0.06) || off < 0.6) c = mix(hex("#ff5ad8"), hex("#3ef0e0"), depth);
      }
      if (th.kind === "cave" && hash2(x, y, 95) < 0.003) c = hex("#5ae8d0");
      if (th.kind === "autumn" && Math.sin((y - HORIZON) * (1.6 - depth) + vnoise(x / 40, y / 10, 7) * 2) > 0.8) c = g[Math.min(3, i + 1)]!;
      if (th.kind === "autumn" && hash2(x, y, 97) < 0.004) c = [hex("#ff8a3c"), hex("#c8402c"), hex("#ffd060")][Math.floor(hash2(x, y, 98) * 3)]!;
      if (th.kind === "haunted" && fbm(x / (20 + depth * 40), y / (5 + depth * 10), 2, seed + 11) > 0.6) c = mix(c, hex("#c8c0e0"), 0.35);
      if (th.kind === "jungle" && hash2(x, y, 99) < 0.004) c = hex("#ff5a8a");
      if (th.kind === "blossom" && hash2(x, y, 100) < 0.01) c = hash2(x, y, 101) > 0.5 ? hex("#ffb3cc") : hex("#ffe1ec");
      if (th.kind === "ruins") {
        const rowH = Math.max(3, Math.round(4 + depth * 10));
        const row = Math.floor((y - HORIZON) / rowH);
        const slabW = Math.round(12 + depth * 26);
        if ((y - HORIZON) % rowH === 0 || (x - SCREEN_W / 2 + (row % 2) * (slabW >> 1) + 10000) % slabW === 0) c = vnoise(x / 20, y / 8, 12) > 0.6 ? hex("#5fae50") : g[3]!;
      }
      if (th.kind === "reef" && Math.sin((x + y * 2.6) / (5 + depth * 10) + vnoise(x / 30, y / 12, 6) * 3) > 0.9) c = g[Math.min(3, i + 1)]!;
      if (th.kind === "reef" && hash2(x, y, 96) < 0.002) c = [hex("#ff8fa0"), hex("#ffffff"), hex("#ffb070")][Math.floor(hash2(x, y, 97) * 3)]!;
      if (th.kind === "sky") {
        const puff = fbm(x / (18 + depth * 40), y / (6 + depth * 14), 3, seed + 9);
        c = puff > 0.62 ? g[0]! : puff > 0.5 ? g[1]! : puff > 0.4 ? g[2]! : g[3]!;
      }
      put(x, y, c);
    }
  }
  // Platforms.
  for (const [pad, big] of [[FOE_PAD, false], [YOU_PAD, true]] as const) {
    const { x: cx, y: cy, rx, ry } = pad;
    const side = big ? 7 : 5;
    for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry + side); y++) {
      for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
        const nx = (x - cx) / rx;
        const ny = (y - cy) / ry;
        const nyS = (y - cy - side) / ry;
        const top = nx * nx + ny * ny <= 1;
        const body = nx * nx + nyS * nyS <= 1 && y > cy;
        if (!top && !body) continue;
        let c: Col;
        if (top) {
          const lam = -nx * 0.5 - ny * 0.7;
          const k = lam > 0.35 ? 0 : lam > -0.3 ? 1 : 2;
          c = th.pad[k]!;
          const r2 = nx * nx + ny * ny;
          if (r2 > 0.86) c = mix(c, hex("#000000"), 0.18);
          if (hash2(x, y, 7) < 0.06) c = th.pad[Math.min(2, k + 1)]!;
        } else {
          c = mix(th.padSide, hex("#000000"), (y - cy) / (ry + side) * 0.35 + (nx > 0.3 ? 0.15 : 0));
          if ((x + y) % 5 === 0) c = mix(c, hex("#000000"), 0.12);
        }
        put(x, y, c);
      }
    }
    // Soft ground shadow ring under the pad.
    for (let x = Math.floor(cx - rx - 6); x <= cx + rx + 8; x++) {
      const yy = Math.round(cy + side + Math.sqrt(Math.max(0, 1 - ((x - cx - 3) / (rx + 5)) ** 2)) * ry) + 1;
      if (yy >= 0 && yy < SCREEN_H) {
        const o = (yy * SCREEN_W + x) * 4;
        if (x >= 0 && x < SCREEN_W) { d[o] = d[o]! * 0.7; d[o + 1] = d[o + 1]! * 0.7; d[o + 2] = d[o + 2]! * 0.75; }
      }
    }
  }
  cache.set(key, img);
  return img;
}

/** Draw the stage with an intro slide: the foe half slides from the left, ours from the right. */
export function drawStage(g: Gfx, zone: ZoneId, tod: Tod, slide: number): void {
  const img = battleBackdrop(zone, tod);
  if (slide >= 1) {
    g.copyRegion(img, 0, 0, SCREEN_W, SCREEN_H, 0, 0);
    return;
  }
  // Sky stays; ground halves slide in opposite directions.
  const off = Math.round((1 - slide) * 180);
  g.copyRegion(img, 0, 0, SCREEN_W, HORIZON + 30, 0, 0);
  g.copyRegion(img, 0, HORIZON + 30, SCREEN_W, SCREEN_H - HORIZON - 30, 0, HORIZON + 30);
  g.fill(0, 0, SCREEN_W, SCREEN_H, [8, 8, 20, Math.round(200 * (1 - slide))]);
  void off;
}
